import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useUser } from '@sde/widgets-core/src/redux/resources/user';
import { styled } from '@uhg-abyss/web/tools/styled';
import {
  useProviderPreferenceQuery,
  formatVariables,
} from '@sde/widgets-core/src/common/hooks/useProviderPreferenceQuery';
import { useHeaderMenu } from 'src/state/headerMenu';
import { event } from '@uhg-abyss/web/tools/event';
import { getFeature } from '@sde/widgets-core/src/redux/tools/features';
import { WelcomeText } from '../WelcomeText';
import {
  DigitalSecurityError,
  LegacyDigitalSecurityError,
} from '../DigitalSecurityError';
import { CustomizeHomePageDrawerWidget } from '../CustomizeHomePageDrawerWidget';
import { PaperlessDeliveryUserCard } from '../PaperlessDeliveryUserCard';

const WelcomeContentContainer = styled('div', {
  opacity: ({ isReady }) => (isReady ? 1 : 0),
  transitionProperty: 'opacity',
  transitionDuration: '800ms',
});

const Subtitle = styled('div', {
  fontWeight: 'bold',
  order: '2',
  flex: '0 1 75%',
  '> *': {
    width: 'inherit !important',
  },
  alignSelf: 'end',
});

const LinkButton = styled('button', {
  color: '$interactive1',
  fontWeight: 'bold',
  textDecoration: 'underline',
  textDecorationThickness: '2px',
});

const WelcomeHeadingFlex = styled('div', {
  marginBottom: '20px',
  display: 'flex',
  justifyContent: 'space-between',
  variants: {
    subtitles: {
      include: {
        flexWrap: 'wrap',
        '> h2': {
          width: '100%',
          flex: '1 0 100%',
        },
      },
      exclude: {
        flexWrap: 'no-wrap',
      },
    },
  },
  '> h2': {
    order: 1,
  },
  ':nth-child(2)': {
    order: 3,
    width: 'inherit !important',
  },
  ':nth-child(3)': {
    order: 2,
  },
});

export const WelcomeContent = ({
  isReady,
  digitalSecurityError,
  setWidgetTabsOrder,
  setIsTabPreferencesLoading,
  setIsProviderPreferenceLoading,
}) => {
  const { user } = useUser.state();
  const { userRoles } = user;
  const pdoAdmin = userRoles?.['pdo-admin-user'];
  const { togglePayer, toggleProvider } = useHeaderMenu.actions();
  const [digitalSecurityApiError, setDigitalSecurityApiError] = useState({});
  const [, getProviderPreference] = useProviderPreferenceQuery({
    onError: (error) => {
      const { code, message } = error;
      setDigitalSecurityApiError({
        code,
        message,
        isDigitalSecurityError: true,
        isProviderPreferenceError: true,
      });

      setIsProviderPreferenceLoading(false);
    },
    onCompleted: (response) => {
      event.setData({
        careProviderMpin:
          response.servicingProviderMpin ||
          response.provider?.servicingProviderMpin,
      });
      setIsProviderPreferenceLoading(false);
    },
  });

  useEffect(() => {
    if (user.uuid) {
      getProviderPreference({
        variables: formatVariables(user),
      });
    }
  }, [user.uuid]);
  useEffect(() => {
    if (digitalSecurityError) {
      setDigitalSecurityApiError(digitalSecurityError);
    }
  }, [digitalSecurityError]);

  return (
    <WelcomeContentContainer isReady={isReady}>
      <Choose>
        <When condition={digitalSecurityApiError.isDigitalSecurityError}>
          <Choose>
            <When condition={pdoAdmin}>
              <PaperlessDeliveryUserCard />
            </When>
            <Otherwise>
              <Choose>
                <When condition={getFeature('USER_ACCESS_MESSAGING_REDESIGN')}>
                  <DigitalSecurityError
                    code={digitalSecurityApiError.code}
                    message={digitalSecurityApiError.message}
                    isProviderPreferenceError={
                      digitalSecurityApiError.isProviderPreferenceError
                    }
                  />
                </When>
                <Otherwise>
                  <LegacyDigitalSecurityError
                    code={digitalSecurityApiError.code}
                    message={digitalSecurityApiError.message}
                    isProviderPreferenceError={
                      digitalSecurityApiError.isProviderPreferenceError
                    }
                  />
                </Otherwise>
              </Choose>
            </Otherwise>
          </Choose>
        </When>
        <Otherwise>
          <WelcomeHeadingFlex subtitles="include">
            <WelcomeText />
            <CustomizeHomePageDrawerWidget
              setWidgetTabsOrder={setWidgetTabsOrder}
              setIsTabPreferencesLoading={setIsTabPreferencesLoading}
            />
            <Subtitle data-testid="welcome-content-subtitle">
              Before you get started, make sure your{' '}
              <LinkButton onClick={() => togglePayer(true)}>
                payer information{' '}
              </LinkButton>{' '}
              and{' '}
              <LinkButton onClick={() => toggleProvider(true)}>
                {' '}
                provider information{' '}
              </LinkButton>{' '}
              in the top right corner of the page are correct.
            </Subtitle>
          </WelcomeHeadingFlex>
        </Otherwise>
      </Choose>
    </WelcomeContentContainer>
  );
};

WelcomeContent.propTypes = {
  isReady: PropTypes.bool.isRequired,
  digitalSecurityError: PropTypes.shape({}).isRequired,
  setWidgetTabsOrder: PropTypes.func.isRequired,
  setIsTabPreferencesLoading: PropTypes.func.isRequired,
  setIsProviderPreferenceLoading: PropTypes.func.isRequired,
};
